// Widget belajar Bilangan: garis bilangan pecahan, membandingkan & mengurutkan pecahan,
// kisi seratus, rantai pecahan-desimal-persen, label diskon, jumlah/kurang pecahan,
// pecahan campuran, model luas, pembagian pecahan, lompat kelipatan (KPK), susun faktor,
// faktor sekutu (FPB), pilah soal cerita, urutan operasi hitung, dan model batang.
import { F, pecahan, simp, fmt, pengatur, pasangPengatur, letup, goyang, animasi, gerakHalus, jeda, batas } from '../alat.js';
import { svgBatang } from './pecahan.js';
import { mix } from '../../core/format.js';
import { lcm, divisors } from '../../core/math.js';
import { shuffle } from '../../core/random.js';

// ---------- perkakas lokal ----------
const rp = n => 'Rp' + Math.round(n).toLocaleString('id-ID');
const campur = (n, d) => { const [a, b] = simp(n, d); return b === 1 ? `${a}` : a > b ? mix(Math.floor(a / b), a % b, b) : pecahan(a, b); };
const himpunan = n => new Set(Array.from({ length: n }, (_, i) => i));

// Klik atau Enter/Spasi pada elemen yang cocok dengan `pilih`.
function ketuk(host, pilih, fn) {
  host.addEventListener('click', e => { const el = e.target.closest(pilih); if (el && host.contains(el) && !el.disabled) fn(el, e); });
  host.addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const el = e.target.closest(pilih);
    if (!el || !host.contains(el) || el.tagName === 'BUTTON') return; // tombol sudah memicu click sendiri
    e.preventDefault();
    fn(el, e);
  });
}

// Potongan yang "terbang" dari satu elemen ke elemen lain (ditempel ke body supaya tidak
// terpengaruh transformasi slide).
async function terbang(dari, ke, kelas = '') {
  if (!gerakHalus() || !dari || !ke) return;
  const a = dari.getBoundingClientRect(), b = ke.getBoundingClientRect();
  const el = document.createElement('div');
  el.className = `wb-terbang ${kelas}`;
  Object.assign(el.style, { left: a.left + 'px', top: a.top + 'px', width: a.width + 'px', height: a.height + 'px' });
  document.body.append(el);
  const dx = b.left - a.left, dy = b.top - a.top;
  await el.animate([
    { transform: 'translate(0,0)' },
    { transform: `translate(${dx / 2}px, ${dy / 2 - 46}px) scale(1.12)`, offset: 0.5 },
    { transform: `translate(${dx}px, ${dy}px) scale(${b.width / a.width || 1}, ${b.height / a.height || 1})` },
  ], { duration: 560, easing: 'cubic-bezier(.4,0,.2,1)' }).finished.catch(() => {});
  el.remove();
}

// Batang pecahan berlabel (dipakai banyak widget).
const baris = (label, svg, kelas = '') => `<div class="wg-baris ${kelas}"><span class="wg-tag">${label}</span><div class="wb-batang-wadah">${svg}</div></div>`;
const batang = (d, a, opsi = {}) => svgBatang(d, opsi.set || himpunan(a), { w: 320, h: 50, ...opsi });

// ==================================================================
// 1. Garis bilangan pecahan: tempatkan kartu pecahan di titik yang tepat.
//    param: { pecahan: [[1,2],[1,4],[3,4]] }
// ==================================================================
export function garisPecahan(host, p, ctx) {
  const L = p.pecahan.reduce((m, [, b]) => lcm(m, b), 1);
  const posisi = k => 6 + (k / L) * 88; // persen dari lebar area
  const kartu = p.pecahan.map(([a, b], i) => ({ a, b, i, beres: false }));
  let pilih = null;
  host.innerHTML = `<div class="wg wb-garis">
    <div class="wb-baki" role="group" aria-label="Kartu pecahan">${shuffle(kartu).map(k => `<button type="button" class="wb-kartu" data-kartu="${k.i}" aria-pressed="false">${pecahan(k.a, k.b)}</button>`).join('')}</div>
    <div class="wb-garis-area">
      <div class="wb-garis-jalur" aria-hidden="true"></div>
      ${Array.from({ length: L + 1 }, (_, k) => `<button type="button" class="wb-tik${k === 0 || k === L ? ' ujung' : ''}" data-k="${k}" style="left:${posisi(k)}%" aria-label="Titik ke-${k} dari ${L} bagian">${k === 0 ? '<span>0</span>' : k === L ? '<span>1</span>' : ''}</button>`).join('')}
    </div>
    <p class="wb-catatan">Jarak 0 sampai 1 dibagi menjadi <b>${L}</b> bagian sama besar. Pilih kartu, lalu ketuk titiknya (atau seret kartunya ke garis).</p>
  </div>`;
  const area = host.querySelector('.wb-garis-area');
  const setPilih = btn => {
    pilih = btn;
    host.querySelectorAll('.wb-kartu').forEach(b => b.setAttribute('aria-pressed', String(b === pilih)));
  };
  let abaikanKlik = false;
  ketuk(host.querySelector('.wb-baki'), '.wb-kartu', b => {
    if (abaikanKlik) { abaikanKlik = false; return; }
    setPilih(pilih === b ? null : b);
    if (pilih) ctx.ucap(`Sekarang ketuk titik untuk ${pecahan(kartu[+b.dataset.kartu].a, kartu[+b.dataset.kartu].b)} di garis.`, 'happy');
  });
  const terdekat = x => { const r = area.getBoundingClientRect(); return batas(Math.round((((x - r.left) / r.width * 100 - 6) / 88) * L), 0, L); };
  const tempatkan = k => {
    if (!pilih) { ctx.ucap('Pilih dulu kartu pecahannya, lalu ketuk titik di garis.', 'think'); return; }
    const c = kartu[+pilih.dataset.kartu];
    const bendera = document.createElement('div');
    bendera.className = 'wb-bendera';
    bendera.style.left = posisi(k) + '%';
    bendera.innerHTML = `<span>${pecahan(c.a, c.b)}</span>`;
    area.append(bendera);
    if (k * c.b === c.a * L) {
      c.beres = true;
      bendera.classList.add('ok');
      pilih.remove(); setPilih(null);
      ctx.sfx.ok();
      animasi(bendera, [{ transform: 'translate(-50%,-40px)', opacity: 0 }, { transform: 'translate(-50%,0)', opacity: 1 }], { duration: 420, easing: 'cubic-bezier(.3,1.5,.6,1)' });
      if (kartu.every(x => x.beres)) ctx.selesai(`Semua tepat! Makin ke kanan, pecahannya makin besar: ${[...kartu].sort((x, y) => x.a / x.b - y.a / y.b).map(x => pecahan(x.a, x.b)).join(' &lt; ')}.`);
      else ctx.ucap(`Tepat! ${pecahan(c.a, c.b)} ada di titik ke-${k} dari ${L} bagian.`, 'cheer');
    } else {
      bendera.classList.add('salah');
      ctx.sfx.no();
      goyang(bendera);
      ctx.ucap(k / L > c.a / c.b ? `Terlalu ke kanan. ${pecahan(c.a, c.b)} lebih kecil dari itu.` : `Terlalu ke kiri. ${pecahan(c.a, c.b)} lebih besar dari itu.`, 'think');
      setTimeout(() => bendera.remove(), 900);
    }
  };
  // Seret kartu ke garis (dengan bayangan yang mengikuti jari).
  let seretan = null;
  host.addEventListener('pointerdown', e => {
    const b = e.target.closest('.wb-kartu');
    if (!b) return;
    seretan = { b, x: e.clientX, y: e.clientY, jalan: false, bayang: null };
    b.setPointerCapture?.(e.pointerId);
  });
  host.addEventListener('pointermove', e => {
    if (!seretan) return;
    if (!seretan.jalan && Math.hypot(e.clientX - seretan.x, e.clientY - seretan.y) > 8) {
      seretan.jalan = true;
      setPilih(seretan.b);
      seretan.bayang = Object.assign(document.createElement('div'), { className: 'wb-bayang', innerHTML: seretan.b.innerHTML });
      document.body.append(seretan.bayang);
    }
    if (seretan.jalan) {
      e.preventDefault();
      Object.assign(seretan.bayang.style, { left: e.clientX + 'px', top: e.clientY + 'px' });
      const k = terdekat(e.clientX);
      area.querySelectorAll('.wb-tik').forEach(t => t.classList.toggle('dekat', +t.dataset.k === k));
    }
  });
  const akhiri = e => {
    if (!seretan) return;
    const s = seretan; seretan = null;
    area.querySelectorAll('.wb-tik').forEach(t => t.classList.remove('dekat'));
    if (!s.jalan) return; // ketukan biasa ditangani oleh click
    abaikanKlik = true;
    setTimeout(() => { abaikanKlik = false; }, 400);
    s.bayang?.remove();
    const r = area.getBoundingClientRect();
    if (e.type === 'pointerup' && e.clientY > r.top - 50 && e.clientY < r.bottom + 30) { setPilih(s.b); tempatkan(terdekat(e.clientX)); }
  };
  host.addEventListener('pointerup', akhiri);
  host.addEventListener('pointercancel', akhiri);
  host.querySelectorAll('.wb-kartu').forEach(b => { b.style.touchAction = 'none'; });
  ketuk(area, '.wb-tik', t => tempatkan(+t.dataset.k));
  return () => seretan?.bayang?.remove();
}

// ==================================================================
// 2. Bandingkan dua pecahan dengan menyamakan penyebut.
//    param: { a: [2,3], b: [3,4] }
// ==================================================================
export function bandingPecahan(host, p, ctx) {
  const [[a1, b1], [a2, b2]] = [p.a, p.b];
  const L = lcm(b1, b2);
  let sama = false;
  host.innerHTML = `<div class="wg wb-banding">
    <div class="wb-batang-dua">
      <div class="wb-satu">${baris(pecahan(a1, b1), batang(b1, a1, { kelas: 'acuan' }), 'kiri')}</div>
      <div class="wb-satu">${baris(pecahan(a2, b2), batang(b2, a2), 'kanan')}</div>
    </div>
    <button type="button" class="btn btn-sky" data-samakan>Samakan penyebutnya</button>
    <div class="wb-perbandingan"><span class="wb-besar">${pecahan(a1, b1)}</span><span class="wb-simbol" aria-live="polite">?</span><span class="wb-besar">${pecahan(a2, b2)}</span></div>
    <div class="wg-tombol" role="group" aria-label="Pilih tanda">${['<', '=', '>'].map(s => `<button type="button" class="btn wb-tanda" data-tanda="${s}" aria-label="${{ '<': 'lebih kecil dari', '=': 'sama dengan', '>': 'lebih besar dari' }[s]}">${s}</button>`).join('')}</div>
  </div>`;
  const [kiri, kanan] = host.querySelectorAll('.wb-satu');
  host.querySelector('[data-samakan]').addEventListener('click', async e => {
    if (sama) return;
    sama = true;
    e.target.disabled = true;
    const k1 = L / b1, k2 = L / b2;
    kiri.innerHTML = baris(pecahan(a1 * k1, L), batang(L, a1 * k1, { kelas: 'acuan baru' }), 'kiri');
    await jeda(250);
    kanan.innerHTML = baris(pecahan(a2 * k2, L), batang(L, a2 * k2, { kelas: 'baru' }), 'kanan');
    ctx.sfx.tap();
    ctx.ucap(`${pecahan(a1, b1)} = ${pecahan(a1 * k1, L)} dan ${pecahan(a2, b2)} = ${pecahan(a2 * k2, L)}. Sekarang tinggal bandingkan pembilangnya: ${a1 * k1} dan ${a2 * k2}.`, 'wow');
  });
  ketuk(host, '[data-tanda]', b => {
    const v = a1 * b2 - a2 * b1;
    const benar = { '<': v < 0, '=': v === 0, '>': v > 0 }[b.dataset.tanda];
    if (benar) {
      host.querySelector('.wb-simbol').textContent = b.dataset.tanda;
      letup(host.querySelector('.wb-simbol'));
      host.querySelectorAll('[data-tanda]').forEach(x => { x.disabled = true; x.classList.toggle('btn-go', x === b); });
      ctx.selesai(`Betul! ${pecahan(a1, b1)} ${b.dataset.tanda} ${pecahan(a2, b2)}, karena ${pecahan(a1 * L / b1, L)} ${b.dataset.tanda} ${pecahan(a2 * L / b2, L)}.`);
    } else {
      ctx.sfx.no(); goyang(b);
      ctx.ucap(sama ? 'Lihat lagi panjang batangnya dan bandingkan pembilangnya.' : 'Susah ditebak, ya? Tekan **Samakan penyebutnya** dulu.', 'think');
    }
  });
}

// ==================================================================
// 3. Urutkan pecahan: ketuk kartu dari yang terkecil (atau terbesar).
//    param: { pecahan: [[2,3],[1,2],[1,6],[3,4]], arah: 'naik'|'turun' }
// ==================================================================
export function urutkanPecahan(host, p, ctx) {
  const turun = p.arah === 'turun';
  const kartu = p.pecahan.map(([a, b], i) => ({ a, b, i }));
  const urut = [...kartu].sort((x, y) => (x.a / x.b - y.a / y.b) * (turun ? -1 : 1));
  let n = 0;
  host.innerHTML = `<div class="wg wb-urut">
    <div class="wb-baki">${shuffle(kartu).map(k => `<button type="button" class="wb-kartu tinggi" data-kartu="${k.i}">${pecahan(k.a, k.b)}<span class="wb-mini" hidden>${svgBatang(k.b, himpunan(k.a), { w: 90, h: 16 })}</span></button>`).join('')}</div>
    <div class="wb-jawaban" aria-live="polite">${urut.map((_, j) => `<span class="wb-slot" data-slot="${j}">${j + 1}</span>`).join(`<span class="wb-panah">${turun ? '&gt;' : '&lt;'}</span>`)}</div>
    <label class="saklar wb-bantu"><input type="checkbox" data-bantu><span>Bantu aku: tampilkan batangnya</span></label>
  </div>`;
  host.querySelector('[data-bantu]').addEventListener('change', e => host.querySelectorAll('.wb-mini').forEach(m => { m.hidden = !e.target.checked; }));
  ketuk(host, '.wb-kartu', async b => {
    const k = kartu[+b.dataset.kartu];
    if (k !== urut[n]) {
      ctx.sfx.no(); goyang(b);
      ctx.ucap(`Belum. Masih ada pecahan yang lebih ${turun ? 'besar' : 'kecil'} dari ${pecahan(k.a, k.b)}. Coba samakan penyebutnya atau nyalakan bantuan batang.`, 'think');
      return;
    }
    const slot = host.querySelector(`[data-slot="${n}"]`);
    n++;
    b.disabled = true;
    await terbang(b, slot, 'kartu');
    slot.innerHTML = pecahan(k.a, k.b);
    slot.classList.add('isi');
    b.classList.add('pakai');
    ctx.sfx.tap();
    letup(slot);
    if (n === urut.length) ctx.selesai(`Urutan yang benar: ${urut.map(x => pecahan(x.a, x.b)).join(turun ? ' &gt; ' : ' &lt; ')}. Keren!`);
    else ctx.ucap(`Bagus! Sekarang yang ${turun ? 'terbesar' : 'terkecil'} berikutnya?`, 'happy');
  });
}

// ==================================================================
// 4. Kisi seratus: arsir kotak; lihat pecahan, desimal, dan persen.
//    param: { target: 25 }
// ==================================================================
export function kisi100(host, p, ctx) {
  const on = new Array(100).fill(false);
  host.innerHTML = `<div class="wg wb-kisi">
    <div class="wb-kisi-grid" role="img" aria-label="Kisi 10 kali 10">${on.map((_, i) => `<span data-c="${i}"></span>`).join('')}</div>
    <div class="wg-tombol" role="group" aria-label="Atur banyak kotak">${[-10, -1, 1, 10].map(v => `<button type="button" class="btn btn-sm" data-tambah="${v}">${v > 0 ? '+' : '−'}${Math.abs(v)}</button>`).join('')}<button type="button" class="btn btn-sm" data-hapus>Hapus</button></div>
    <div class="wb-tiga" aria-live="polite"><div><small>Pecahan</small><b data-pec></b></div><div><small>Desimal</small><b data-des></b></div><div><small>Persen</small><b data-per></b></div></div>
  </div>`;
  const grid = host.querySelector('.wb-kisi-grid'), sel = [...grid.children];
  const tampil = () => {
    const n = on.filter(Boolean).length;
    sel.forEach((c, i) => c.classList.toggle('on', on[i]));
    const [a, b] = simp(n, 100);
    host.querySelector('[data-pec]').innerHTML = n === 0 ? '0' : n === 100 ? '1' : `${pecahan(n, 100)}${b !== 100 ? ` = ${pecahan(a, b)}` : ''}`;
    host.querySelector('[data-des]').textContent = (n / 100).toFixed(2).replace('.', ',');
    host.querySelector('[data-per]').textContent = `${n}%`;
    return n;
  };
  const cek = async () => {
    const n = tampil();
    if (n === p.target) {
      const [a, b] = simp(n, 100);
      if (gerakHalus()) sel.forEach((c, i) => { if (on[i]) c.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }], { duration: 400, delay: (i % 10 + Math.floor(i / 10)) * 18 }); });
      ctx.selesai(`${n} dari 100 kotak = ${pecahan(n, 100)}${b !== 100 ? ` = ${pecahan(a, b)}` : ''} = ${(n / 100).toFixed(2).replace('.', ',')} = **${n}%**.`);
    } else if (n > p.target) ctx.ucap(`Sekarang ${n}%. Kebanyakan, kurangi sedikit.`, 'wow');
    else ctx.ucap(n ? `Sekarang ${n} kotak = ${n}%.` : '');
  };
  // Arsir dengan seret: kotak pertama menentukan mode (mengarsir atau menghapus).
  let mode = null, terakhir = -1;
  const kena = e => { const el = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('[data-c]'); return el && grid.contains(el) ? +el.dataset.c : -1; };
  grid.style.touchAction = 'none';
  grid.addEventListener('pointerdown', e => {
    const i = kena(e);
    if (i < 0) return;
    mode = !on[i]; terakhir = i; on[i] = mode;
    grid.setPointerCapture?.(e.pointerId);
    tampil(); ctx.sfx.tap();
  });
  grid.addEventListener('pointermove', e => {
    if (mode === null) return;
    const i = kena(e);
    if (i >= 0 && i !== terakhir) { terakhir = i; on[i] = mode; tampil(); }
  });
  const lepas = () => { if (mode === null) return; mode = null; cek(); };
  grid.addEventListener('pointerup', lepas);
  grid.addEventListener('pointercancel', lepas);
  host.querySelectorAll('[data-tambah]').forEach(b => b.addEventListener('click', () => {
    let n = on.filter(Boolean).length + Number(b.dataset.tambah);
    n = batas(n, 0, 100);
    on.fill(false); for (let i = 0; i < n; i++) on[i] = true;
    ctx.sfx.tap(); cek();
  }));
  host.querySelector('[data-hapus]').addEventListener('click', () => { on.fill(false); cek(); });
  tampil();
}

// ==================================================================
// 5. Rantai pecahan → per seratus → desimal → persen, langkah demi langkah.
//    param: { pecahan: [[3,4],[2,5],[1,20]], coba: 2 }
// ==================================================================
export function rantaiKonversi(host, p, ctx) {
  const selesaiSet = new Set();
  let kini = null, langkah = 0;
  host.innerHTML = `<div class="wg wb-rantai">
    <div class="wg-tombol" role="group" aria-label="Pilih pecahan">${p.pecahan.map(([a, b], i) => `<button type="button" class="btn btn-sm wb-pilih" data-p="${i}">${pecahan(a, b)}</button>`).join('')}</div>
    <div class="wb-rantai-jalur" aria-live="polite"></div>
    <button type="button" class="btn btn-sun" data-lanjut disabled>Langkah berikutnya ▶</button>
  </div>`;
  const jalur = host.querySelector('.wb-rantai-jalur'), lanjut = host.querySelector('[data-lanjut]');
  const simpul = (isi, ket, kelas = '') => `<div class="wb-simpul ${kelas}"><b>${isi}</b><small>${ket}</small></div>`;
  const tampilLangkah = () => {
    const [a, b] = p.pecahan[kini], k = 100 / b, n = a * k;
    const bag = [
      simpul(pecahan(a, b), 'pecahan awal'),
      simpul(`${pecahan(`${a} × ${k}`, `${b} × ${k}`)}`, `kali ${k} supaya penyebutnya 100`),
      simpul(pecahan(n, 100), `${n} per seratus`),
      simpul((n / 100).toFixed(2).replace('.', ','), 'per seratus = 2 angka di belakang koma'),
      simpul(`${n}%`, 'per seratus = persen', 'akhir'),
    ];
    jalur.innerHTML = bag.slice(0, langkah + 1).join('<span class="wb-rantai-panah" aria-hidden="true">→</span>');
    const baru = jalur.lastElementChild;
    animasi(baru, [{ opacity: 0, transform: 'translateX(-14px) scale(.9)' }, { opacity: 1, transform: 'none' }], { duration: 360 });
    lanjut.disabled = langkah >= bag.length - 1;
    if (langkah >= bag.length - 1) {
      selesaiSet.add(kini);
      host.querySelector(`[data-p="${kini}"]`).classList.add('btn-go');
      if (selesaiSet.size >= (p.coba || 2)) ctx.selesai(`Mantap! Caranya selalu sama: ubah penyebutnya menjadi **100**, lalu ${pecahan(n, 100)} = ${(n / 100).toFixed(2).replace('.', ',')} = ${n}%.`);
      else ctx.ucap(`${pecahan(a, b)} = ${n}%. Sekarang coba pecahan yang lain!`, 'cheer');
    }
  };
  ketuk(host, '[data-p]', b => {
    kini = +b.dataset.p; langkah = 0;
    host.querySelectorAll('[data-p]').forEach(x => x.classList.toggle('btn-sun', x === b));
    ctx.sfx.tap(); tampilLangkah();
    ctx.ucap('Tekan **Langkah berikutnya** untuk melihat perubahannya.', 'happy');
  });
  lanjut.addEventListener('click', () => { langkah++; ctx.sfx.tap(); tampilLangkah(); });
}

// ==================================================================
// 6. Label harga diskon: geser persen diskon sampai harga akhirnya tepat.
//    param: { harga: 80000, targetAkhir: 60000, maks: 50 }
// ==================================================================
export function labelHarga(host, p, ctx) {
  host.innerHTML = `<div class="wg wb-harga">
    <div class="wb-label" aria-live="polite">
      <span class="wb-lubang" aria-hidden="true"></span>
      <small>Harga awal</small><s data-awal>${rp(p.harga)}</s>
      <span class="wb-diskon" data-diskon>Diskon 0%</span>
      <small>Bayar</small><b data-akhir>${rp(p.harga)}</b>
    </div>
    <label class="wb-geser"><span>Diskon: <b data-nilai>0%</b></span><input type="range" min="0" max="${p.maks || 50}" step="5" value="0" aria-label="Persen diskon"></label>
    <div class="wb-hitung" aria-live="polite"></div>
  </div>`;
  const inp = host.querySelector('input'), labelEl = host.querySelector('.wb-label');
  const ubah = () => {
    const d = +inp.value, potong = p.harga * d / 100, akhir = p.harga - potong;
    host.querySelector('[data-nilai]').textContent = `${d}%`;
    host.querySelector('[data-diskon]').textContent = `Diskon ${d}%`;
    host.querySelector('[data-akhir]').textContent = rp(akhir);
    labelEl.classList.toggle('ada-diskon', d > 0);
    host.querySelector('.wb-hitung').innerHTML = d ? `Potongan = ${d}% × ${rp(p.harga)} = ${pecahan(d, 100)} × ${fmt(p.harga)} = <b>${rp(potong)}</b><br>Bayar = ${rp(p.harga)} − ${rp(potong)} = <b>${rp(akhir)}</b>` : 'Geser untuk memberi diskon.';
    animasi(labelEl, [{ transform: 'rotate(-4deg)' }, { transform: 'rotate(3deg)' }, { transform: 'rotate(0)' }], { duration: 420 });
    if (akhir === p.targetAkhir) ctx.selesai(`Betul! Diskon **${d}%** dari ${rp(p.harga)} adalah ${rp(potong)}, jadi yang dibayar ${rp(akhir)}.`);
    else if (akhir < p.targetAkhir) ctx.ucap(`Wah, jadi ${rp(akhir)}. Terlalu murah! Kurangi diskonnya.`, 'wow');
    else ctx.ucap(d ? `Potongannya ${rp(potong)}. Harga akhirnya masih di atas ${rp(p.targetAkhir)}.` : '');
  };
  inp.addEventListener('input', ubah);
  ubah();
  ctx.ucap('');
}

// ==================================================================
// 7. Jumlah/kurang pecahan berpenyebut sama dengan memindah atau mengambil potongan.
//    param: { mode: 'tambah'|'kurang', a, b, d }
// ==================================================================
export function gabungPecahan(host, p, ctx) {
  const { a, b, d } = p, tambah = p.mode !== 'kurang';
  let pindah = 0;
  host.innerHTML = tambah ? `<div class="wg wb-gabung">
      ${baris(pecahan(a, d), batang(d, a, { kelas: 'acuan' }))}
      <div class="wb-plus" aria-hidden="true">+</div>
      <div class="wb-sumber">${baris(pecahan(b, d), batang(d, b, { bisaKetuk: true, kelas: 'sumber' }))}</div>
      <div class="wb-sama" aria-hidden="true">=</div>
      <div class="wb-hasil-baris"></div>
      <button type="button" class="btn btn-sm" data-semua>Pindahkan semua</button>
    </div>` : `<div class="wg wb-gabung">
      <div class="wb-sumber">${baris(pecahan(a, d), batang(d, a, { bisaKetuk: true, kelas: 'sumber kurang' }))}</div>
      <p class="wb-catatan">Ketuk ${b} potong yang diarsir untuk mengambilnya.</p>
      <div class="wb-hasil-baris" hidden></div>
    </div>`;
  const hasil = host.querySelector('.wb-hasil-baris'), sumber = host.querySelector('.wb-sumber');
  const akhir = () => {
    if (tambah) {
      hasil.querySelector('.wg-tag').innerHTML = pecahan(a + b, d);
      ctx.selesai(`${pecahan(a, d)} + ${pecahan(b, d)} = ${pecahan(a + b, d)}. Penyebutnya tetap ${d}, pembilangnya dijumlah: ${a} + ${b} = ${a + b}.`);
    } else {
      hasil.hidden = false;
      hasil.innerHTML = `<div class="wb-persamaan-besar">${pecahan(a, d)} − ${pecahan(b, d)} = ${pecahan(a - b, d)}</div>`;
      animasi(hasil, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }]);
      ctx.selesai(`Tersisa ${a - b} potong: ${pecahan(a, d)} − ${pecahan(b, d)} = ${pecahan(a - b, d)}. Penyebutnya tetap, pembilangnya dikurang.`);
    }
  };
  const ambil = async el => {
    if (!el.classList.contains('on') || el.dataset.diambil) return;
    if (!tambah && pindah >= b) return;
    el.dataset.diambil = '1';
    if (tambah) {
      const target = hasil.querySelectorAll('.wg-bagian')[a + pindah];
      pindah++;
      el.classList.remove('on'); el.classList.add('kosong');
      await terbang(el, target, 'potong');
      target.classList.add('on', 'pindahan');
      hasil.querySelector('.wg-tag').innerHTML = pecahan(a + pindah, d);
      ctx.sfx.tap();
      if (pindah === b) akhir();
    } else {
      pindah++;
      animasi(el, [{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-26px) rotate(-12deg)', opacity: 0 }], { duration: 380 }).then(() => { el.classList.remove('on'); el.classList.add('kosong'); });
      sumber.querySelector('.wg-tag').innerHTML = pecahan(a - pindah, d);
      ctx.sfx.tap();
      if (pindah === b) akhir();
      else ctx.ucap(`Sudah diambil ${pindah} potong. Ambil ${b - pindah} lagi.`);
    }
  };
  // Tahap awal hasil = pecahan pertama.
  if (tambah) hasil.innerHTML = baris(pecahan(a, d), batang(d, a, { kelas: 'acuan' }), 'hasil');
  ketuk(sumber, '.wg-bagian', el => ambil(el));
  host.querySelector('[data-semua]')?.addEventListener('click', async e => {
    e.target.disabled = true;
    for (const el of [...sumber.querySelectorAll('.wg-bagian.on')]) { await ambil(el); await jeda(80); }
  });
}

// ==================================================================
// 8. Samakan penyebut sebelum menjumlah: pilih penyebut baru (KPK).
//    param: { a: [1,2], b: [1,3], op: '+'|'−', pilihan: [5,6,12] }
// ==================================================================
export function samakanPenyebut(host, p, ctx) {
  const [[a1, b1], [a2, b2]] = [p.a, p.b], kurang = p.op === '−';
  const K = lcm(b1, b2);
  host.innerHTML = `<div class="wg wb-samakan">
    <div class="wb-persamaan-besar" data-soal>${pecahan(a1, b1)} ${kurang ? '−' : '+'} ${pecahan(a2, b2)} = ?</div>
    <div class="wb-satu">${baris(pecahan(a1, b1), batang(b1, a1, { kelas: 'acuan' }))}</div>
    <div class="wb-satu">${baris(pecahan(a2, b2), batang(b2, a2))}</div>
    <p class="wb-catatan">Pilih penyebut baru yang bisa dipakai kedua pecahan:</p>
    <div class="wg-tombol">${p.pilihan.map(n => `<button type="button" class="btn wb-pil" data-n="${n}">${n}</button>`).join('')}</div>
    <div class="wb-hasil-baris" hidden></div>
  </div>`;
  const [s1, s2] = host.querySelectorAll('.wb-satu'), hasil = host.querySelector('.wb-hasil-baris');
  ketuk(host, '[data-n]', async btn => {
    const n = +btn.dataset.n;
    if (n % b1 || n % b2) {
      ctx.sfx.no(); goyang(btn);
      ctx.ucap(`${n} bukan kelipatan ${b1} dan ${b2}, jadi kedua batang tidak bisa dipotong rata menjadi ${n} bagian.`, 'think');
      return;
    }
    const x1 = a1 * n / b1, x2 = a2 * n / b2, r = kurang ? x1 - x2 : x1 + x2;
    s1.innerHTML = baris(pecahan(x1, n), batang(n, x1, { kelas: 'acuan baru' }));
    await jeda(200);
    s2.innerHTML = baris(pecahan(x2, n), batang(n, x2, { kelas: 'baru' }));
    hasil.hidden = false;
    hasil.innerHTML = `<div class="wb-persamaan-besar">${pecahan(x1, n)} ${kurang ? '−' : '+'} ${pecahan(x2, n)} = <span class="wb-sorot">${pecahan(r, n)}</span>${simp(r, n)[1] !== n ? ` = ${campur(r, n)}` : ''}</div>`;
    animasi(hasil, [{ opacity: 0, transform: 'scale(.9)' }, { opacity: 1, transform: 'none' }]);
    ctx.sfx.tap();
    if (n === K) {
      host.querySelectorAll('[data-n]').forEach(x => { x.disabled = true; x.classList.toggle('btn-go', x === btn); });
      ctx.selesai(`Tepat! **${K}** adalah KPK dari ${b1} dan ${b2}, penyebut bersama yang paling kecil. Jadi ${pecahan(a1, b1)} ${kurang ? '−' : '+'} ${pecahan(a2, b2)} = ${campur(r, n)}.`);
    } else ctx.ucap(`Bisa juga dengan ${n}! Tapi angkanya jadi besar. Ada penyebut bersama yang lebih kecil?`, 'wow');
  });
}

// ==================================================================
// 9. Pecahan campuran → pecahan biasa: potong yang utuh, lalu hitung potongannya.
//    param: { w: 2, n: 1, d: 4 }
// ==================================================================
export function campuran(host, p, ctx) {
  const { w, n, d } = p, total = w * d + n;
  let dihitung = 0, dipotong = false;
  const batangBernomor = (idx, terpotong) => {
    const parts = terpotong || idx === w ? d : 1, isi = idx === w ? n : parts;
    const lebar = 320 / parts;
    return `<svg class="wg-batang${terpotong && idx < w ? ' baru' : ''}" viewBox="0 0 320 46" role="img" aria-label="batang ${idx + 1}">${Array.from({ length: parts }, (_, i) => `<g><rect class="wg-bagian${i < isi ? ' on' : ''}" data-b="${idx}" data-i="${i}" x="${(i * lebar + 1).toFixed(1)}" y="1" width="${(lebar - 2).toFixed(1)}" height="44" rx="6"${dipotong && i < isi ? ' tabindex="0" role="button"' : ''} aria-label="potongan"/><text class="wb-nomor" x="${(i * lebar + lebar / 2).toFixed(1)}" y="30" text-anchor="middle"></text></g>`).join('')}</svg>`;
  };
  host.innerHTML = `<div class="wg wb-campuran">
    <div class="wb-persamaan-besar" data-label>${mix(w, n, d)} = ?</div>
    <div class="wb-tumpuk"></div>
    <button type="button" class="btn btn-sky" data-potong>Potong yang utuh menjadi ${pecahan(1, d)}-an</button>
    <p class="wb-catatan" data-catat>Ada ${w} batang utuh dan ${pecahan(n, d)} batang.</p>
  </div>`;
  const tumpuk = host.querySelector('.wb-tumpuk');
  const gambar = () => { tumpuk.innerHTML = Array.from({ length: w + 1 }, (_, i) => batangBernomor(i, dipotong)).join(''); };
  gambar();
  host.querySelector('[data-potong]').addEventListener('click', e => {
    dipotong = true; e.target.hidden = true; gambar(); ctx.sfx.tap();
    host.querySelector('[data-catat]').innerHTML = 'Sekarang ketuk setiap potongan yang diarsir untuk menghitungnya.';
    ctx.ucap(`Setiap batang utuh = ${pecahan(d, d)}. Ayo hitung semua potongannya!`, 'wow');
  });
  ketuk(tumpuk, '.wg-bagian.on', el => {
    if (!dipotong || el.dataset.sudah) return;
    el.dataset.sudah = '1';
    dihitung++;
    el.classList.add('terhitung');
    el.nextElementSibling.textContent = dihitung;
    letup(el);
    ctx.sfx.tap();
    host.querySelector('[data-label]').innerHTML = `${mix(w, n, d)} = ${pecahan(dihitung, d)}${dihitung < total ? '…' : ''}`;
    if (dihitung === total) ctx.selesai(`Ada ${total} potongan ${pecahan(1, d)}, jadi ${mix(w, n, d)} = ${pecahan(total, d)}. Cara cepatnya: (${w} × ${d} + ${n}) = ${total}.`);
  });
}

// ==================================================================
// 10. Model luas perkalian pecahan.
//     param: { target: [[2,3],[3,4]] }
// ==================================================================
export function modelLuas(host, p, ctx) {
  const st = { a: 1, b: 2, c: 1, d: 2 };
  const [[ta, tb], [tc, td]] = p.target;
  host.innerHTML = `<div class="wg wb-luas">
    <div class="wb-luas-atur">
      <div class="wb-luas-grup biru"><b>Pecahan 1 (tegak)</b>${pengatur('a', 'pembilang', st.a, { min: 1, max: 8 })}${pengatur('b', 'penyebut', st.b, { min: 1, max: 8 })}</div>
      <div class="wb-luas-grup merah"><b>Pecahan 2 (mendatar)</b>${pengatur('c', 'pembilang', st.c, { min: 1, max: 8 })}${pengatur('d', 'penyebut', st.d, { min: 1, max: 8 })}</div>
    </div>
    <div class="wb-luas-gambar"></div>
    <div class="wb-persamaan-besar" aria-live="polite" data-pers></div>
  </div>`;
  const gambar = host.querySelector('.wb-luas-gambar');
  const render = () => {
    const S = 240, cw = S / st.b, ch = S / st.d;
    let g = '';
    for (let r = 0; r < st.d; r++) for (let c = 0; c < st.b; c++) {
      const v = c < st.a, h = r < st.c;
      g += `<rect x="${(c * cw).toFixed(1)}" y="${(r * ch).toFixed(1)}" width="${cw.toFixed(1)}" height="${ch.toFixed(1)}" class="wb-sel${v ? ' v' : ''}${h ? ' h' : ''}${v && h ? ' vh' : ''}"/>`;
    }
    gambar.innerHTML = `<svg viewBox="-4 -4 248 248" role="img" aria-label="persegi dibagi ${st.b} kolom dan ${st.d} baris">${g}<rect x="0" y="0" width="${S}" height="${S}" class="wb-bingkai"/></svg>`;
    const n = st.a * st.c, m = st.b * st.d;
    host.querySelector('[data-pers]').innerHTML = `${pecahan(st.a, st.b)} × ${pecahan(st.c, st.d)} = ${pecahan(`${st.a} × ${st.c}`, `${st.b} × ${st.d}`)} = <span class="wb-sorot">${pecahan(n, m)}</span>`;
    return { n, m };
  };
  const cek = () => {
    const { n, m } = render();
    if (st.a === ta && st.b === tb && st.c === tc && st.d === td) {
      const [x, y] = simp(n, m);
      ctx.selesai(`Kotak ungu (tumpang tindih) ada **${n}** dari **${m}** kotak. Jadi ${pecahan(ta, tb)} × ${pecahan(tc, td)} = ${pecahan(n, m)}${y !== m ? ` = ${pecahan(x, y)}` : ''}.`);
    } else ctx.ucap(`Kotak ungu: ${n} dari ${m} kotak. Atur menjadi ${pecahan(ta, tb)} × ${pecahan(tc, td)}.`);
  };
  pasangPengatur(host, st, id => {
    // Pembilang tidak boleh melebihi penyebut.
    if (st.a > st.b) { st.a = st.b; host.querySelector('[data-pengatur="a"] b').textContent = st.a; }
    if (st.c > st.d) { st.c = st.d; host.querySelector('[data-pengatur="c"] b').textContent = st.c; }
    ctx.sfx.tap();
    cek();
    animasi(gambar, [{ opacity: .4 }, { opacity: 1 }], { duration: 220 });
  });
  render();
}

// ==================================================================
// 11. Berapa banyak 1/d dalam n? Potong batang, hitung potongannya.
//     param: { n: 2, d: 4, pilihan: [2,6,8] }
// ==================================================================
export function bagiPecahan(host, p, ctx) {
  const { n, d } = p, jawab = n * d;
  let hidup = true;
  host.innerHTML = `<div class="wg wb-bagi">
    <div class="wb-persamaan-besar">${n} : ${pecahan(1, d)} = <span data-jawab>?</span></div>
    <p class="wb-catatan">Artinya: ada berapa potong ${pecahan(1, d)} di dalam ${n} batang?</p>
    <div class="wb-tumpuk">${Array.from({ length: n }, () => batang(1, 1)).join('')}</div>
    <button type="button" class="btn btn-sky" data-potong>Potong setiap batang menjadi ${pecahan(1, d)}</button>
    <div class="wg-tombol" data-pilihan hidden>${shuffle(p.pilihan).map(v => `<button type="button" class="btn" data-v="${v}">${v} potong</button>`).join('')}</div>
  </div>`;
  const tumpuk = host.querySelector('.wb-tumpuk');
  host.querySelector('[data-potong]').addEventListener('click', async e => {
    e.target.hidden = true;
    tumpuk.innerHTML = Array.from({ length: n }, () => batang(d, d, { kelas: 'baru' })).join('');
    ctx.sfx.tap();
    // Animasi menghitung: setiap potongan menyala bergantian.
    const semua = [...tumpuk.querySelectorAll('.wg-bagian')];
    for (const [i, el] of semua.entries()) {
      if (!hidup) return;
      await jeda(110);
      el.classList.add('terhitung');
      el.parentNode.insertAdjacentHTML('beforeend', `<text class="wb-nomor" x="${(+el.getAttribute('x') + +el.getAttribute('width') / 2).toFixed(1)}" y="31" text-anchor="middle">${i + 1}</text>`);
    }
    host.querySelector('[data-pilihan]').hidden = false;
    ctx.ucap('Jadi ada berapa potong?', 'think');
  });
  ketuk(host, '[data-v]', b => {
    if (+b.dataset.v === jawab) {
      host.querySelector('[data-jawab]').innerHTML = `<span class="wb-sorot">${jawab}</span>`;
      host.querySelectorAll('[data-v]').forEach(x => { x.disabled = true; x.classList.toggle('btn-go', x === b); });
      ctx.selesai(`${n} : ${pecahan(1, d)} = **${jawab}**. Perhatikan: hasilnya sama dengan ${n} × ${d}. Membagi dengan ${pecahan(1, d)} sama dengan mengali ${d}!`);
    } else { ctx.sfx.no(); goyang(b); ctx.ucap('Hitung lagi nomor potongannya, ya.', 'think'); }
  });
  return () => { hidup = false; };
}

// ==================================================================
// 12. Kali kebalikan: a/b : c/d = a/b × d/c (dengan animasi membalik).
//     param: { a: [3,4], b: [1,2] }
// ==================================================================
export function balikPecahan(host, p, ctx) {
  const [[a, b], [c, d]] = [p.a, p.b];
  host.innerHTML = `<div class="wg wb-balik">
    <div class="wb-persamaan-besar wb-langkah-balik" aria-live="polite"><span>${pecahan(a, b)}</span><span class="wb-op" data-op>:</span><span class="wb-kartu-balik" data-balik>${pecahan(c, d)}</span></div>
    <div class="wg-tombol"><button type="button" class="btn btn-sky" data-tombol-balik>1. Balik pecahan kedua, ubah : menjadi ×</button><button type="button" class="btn btn-sun" data-kali disabled>2. Kalikan</button></div>
    <div class="wb-hasil-baris" hidden></div>
  </div>`;
  const kartu = host.querySelector('[data-balik]'), op = host.querySelector('[data-op]');
  host.querySelector('[data-tombol-balik]').addEventListener('click', async e => {
    e.target.disabled = true;
    await animasi(kartu, [{ transform: 'rotateY(0)' }, { transform: 'rotateY(90deg)' }], { duration: 260, easing: 'ease-in' });
    kartu.innerHTML = F(d, c); // tetap ditulis sebagai pecahan (mis. 2/1) supaya terlihat dibalik
    op.textContent = '×';
    letup(op);
    await animasi(kartu, [{ transform: 'rotateY(-90deg)' }, { transform: 'rotateY(0)' }], { duration: 260, easing: 'ease-out' });
    kartu.classList.add('terbalik');
    host.querySelector('[data-kali]').disabled = false;
    ctx.sfx.tap();
    ctx.ucap(`${pecahan(c, d)} dibalik menjadi ${F(d, c)}. Ini namanya **kebalikan**.`, 'wow');
  });
  host.querySelector('[data-kali]').addEventListener('click', e => {
    e.target.disabled = true;
    const n = a * d, m = b * c;
    const h = host.querySelector('.wb-hasil-baris');
    h.hidden = false;
    h.innerHTML = `<div class="wb-persamaan-besar">= ${pecahan(`${a} × ${d}`, `${b} × ${c}`)} = ${pecahan(n, m)}${simp(n, m)[1] !== m || n > m ? ` = <span class="wb-sorot">${campur(n, m)}</span>` : ''}</div>`;
    animasi(h, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }]);
    ctx.selesai(`${pecahan(a, b)} : ${pecahan(c, d)} = ${pecahan(a, b)} × ${F(d, c)} = ${campur(n, m)}. Membagi pecahan = mengali dengan kebalikannya.`);
  });
}

// ==================================================================
// 13. Lompat katak: kelipatan & KPK.
//     param: { a: 4, b: 6, maks: 36 }  atau satu katak: { a: 3, lompat: 5, maks: 18 }
// ==================================================================
const KATAK = (warna) => `<g class="wb-katak ${warna}"><ellipse cx="0" cy="-11" rx="15" ry="11" class="badan"/><circle cx="-7" cy="-22" r="5.5" class="mata"/><circle cx="7" cy="-22" r="5.5" class="mata"/><circle cx="-6" cy="-22" r="2.4" class="pupil"/><circle cx="8" cy="-22" r="2.4" class="pupil"/><path d="M-7 -8 Q0 -3 7 -8" class="mulut"/><path d="M-15 -2 l-6 3 M15 -2 l6 3" class="kaki"/></g>`;
export function lompatKatak(host, p, ctx) {
  const dua = !!p.b, maks = p.maks || 36;
  const W = 560, x = v => 26 + (v / maks) * (W - 52);
  // Dua garis bilangan sejajar (satu per katak) supaya tempat mendaratnya mudah dibandingkan.
  const GARIS = [80, 176], H = dua ? 210 : 116;
  const katak = [{ loncat: p.a, pos: 0, jejak: new Set([0]), kelas: 'hijau', nama: 'hijau' }];
  if (dua) katak.push({ loncat: p.b, pos: 0, jejak: new Set([0]), kelas: 'oranye', nama: 'oranye' });
  let kpk = null, sibuk = false, lompatan = 0;
  const garis = (y, i) => `<g class="wb-sumbu ${katak[i].kelas}"><line x1="${x(0)}" y1="${y}" x2="${x(maks)}" y2="${y}" class="wb-jalur"/>
      ${Array.from({ length: maks + 1 }, (_, v) => `<line x1="${x(v).toFixed(1)}" y1="${y - (v % 5 === 0 ? 7 : 4)}" x2="${x(v).toFixed(1)}" y2="${y + (v % 5 === 0 ? 7 : 4)}" class="wb-tik-garis"/>`).join('')}
      <text x="${x(0)}" y="${y + 26}" class="wb-angka" text-anchor="middle">0</text><text x="${x(maks)}" y="${y + 26}" class="wb-angka" text-anchor="middle">${maks}</text></g>`;
  host.innerHTML = `<div class="wg wb-katak-wg">
    <svg class="wb-garis-katak" viewBox="0 0 ${W} ${H}" role="img" aria-label="garis bilangan 0 sampai ${maks}">
      <g data-sambung></g>
      ${katak.map((_, i) => garis(GARIS[i], i)).join('')}
      <g data-jejak></g>
      ${katak.map((k, i) => `<g data-katak="${i}" style="transform:translate(${x(0)}px, ${GARIS[i] - 12}px)">${KATAK(k.kelas)}</g>`).join('')}
    </svg>
    <div class="wg-tombol">${katak.map((k, i) => `<button type="button" class="btn wb-lompat ${k.kelas}" data-lompat="${i}">Lompat${dua ? ` katak ${k.nama}` : ''} +${k.loncat}</button>`).join('')}</div>
    <div class="wb-kelipatan" aria-live="polite">${katak.map((k, i) => `<div><span class="wb-titik ${k.kelas}"></span>Kelipatan ${k.loncat}: <b data-daftar="${i}">0</b></div>`).join('')}</div>
  </div>`;
  const svg = host.querySelector('svg'), jejak = svg.querySelector('[data-jejak]'), sambung = svg.querySelector('[data-sambung]');
  const tanda = (v, i) => {
    const y = GARIS[i];
    jejak.insertAdjacentHTML('beforeend', `<g class="wb-jejak ${katak[i].kelas}"><circle cx="${x(v).toFixed(1)}" cy="${y}" r="11"/><text x="${x(v).toFixed(1)}" y="${y + 4}" text-anchor="middle">${v}</text></g>`);
    animasi(jejak.lastElementChild, [{ transform: 'scale(0)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 320 });
    return dua && katak[0].jejak.has(v) && katak[1].jejak.has(v);
  };
  ketuk(host, '[data-lompat]', async btn => {
    if (sibuk || kpk) return;
    const i = +btn.dataset.lompat, k = katak[i], tujuan = k.pos + k.loncat;
    if (tujuan > maks) { ctx.ucap('Katak ini sudah sampai ujung garis.', 'think'); btn.disabled = true; return; }
    sibuk = true;
    const g = svg.querySelector(`[data-katak="${i}"]`), y = GARIS[i] - 12, x0 = x(k.pos), x1 = x(tujuan);
    if (gerakHalus()) await g.animate([
      { transform: `translate(${x0}px, ${y}px)` },
      { transform: `translate(${(x0 + x1) / 2}px, ${y - 34}px) rotate(-10deg)`, offset: 0.5 },
      { transform: `translate(${x1}px, ${y}px)` },
    ], { duration: 520, easing: 'ease-in-out' }).finished.catch(() => {});
    g.style.transform = `translate(${x1}px, ${y}px)`;
    k.pos = tujuan; k.jejak.add(tujuan); lompatan++;
    ctx.sfx.tap();
    host.querySelector(`[data-daftar="${i}"]`).textContent = [...k.jejak].sort((m, n) => m - n).join(', ');
    const sama = tanda(tujuan, i);
    sibuk = false;
    if (!dua) {
      if (lompatan >= (p.lompat || 5)) { btn.disabled = true; ctx.selesai(`Tempat mendarat katak adalah **kelipatan ${k.loncat}**: ${[...k.jejak].filter(v => v).join(', ')}, dan seterusnya. Setiap lompat bertambah ${k.loncat}.`); }
      else ctx.ucap(`Mendarat di ${tujuan}. Lompat lagi!`);
      return;
    }
    if (sama) {
      kpk = tujuan;
      host.querySelectorAll('[data-lompat]').forEach(b => { b.disabled = true; });
      sambung.innerHTML = `<line x1="${x(tujuan)}" y1="${GARIS[0]}" x2="${x(tujuan)}" y2="${GARIS[1]}" class="wb-sambung"/><text x="${x(tujuan)}" y="${(GARIS[0] + GARIS[1]) / 2 + 5}" text-anchor="middle" class="wb-kpk-label">KPK</text>`;
      animasi(sambung, [{ opacity: 0 }, { opacity: 1 }], { duration: 400 });
      jejak.querySelectorAll('.wb-jejak').forEach(el => { if (el.querySelector('text').textContent === String(tujuan)) el.classList.add('sama'); });
      ctx.selesai(`Kedua katak mendarat di angka yang sama: **${tujuan}**! Itu kelipatan persekutuan yang paling kecil, jadi KPK dari ${p.a} dan ${p.b} = ${tujuan}.`);
    } else {
      const [A, B] = katak;
      ctx.ucap(A.pos === B.pos ? '' : `Katak ${A.pos < B.pos ? 'hijau' : 'oranye'} tertinggal di belakang. Lompatkan katak itu!`, 'happy');
    }
  });
}

// ==================================================================
// 14. Susun faktor: atur titik menjadi baris-baris yang sama panjang.
//     param: { n: 12 }
// ==================================================================
export function susunFaktor(host, p, ctx) {
  const n = p.n, st = { r: 1 }, temu = new Set();
  host.innerHTML = `<div class="wg wb-faktor">
    <div class="wg-alat">${pengatur('r', 'Banyak baris', 1, { min: 1, max: n })}</div>
    <div class="wb-titik-area" aria-hidden="true">${Array.from({ length: n }, (_, i) => `<i data-t="${i}"></i>`).join('')}</div>
    <div class="wb-faktor-info" aria-live="polite"></div>
    <div class="wb-temuan-faktor"><span>Faktor ${n} yang ditemukan:</span><span class="wg-chips" data-chips></span></div>
  </div>`;
  const area = host.querySelector('.wb-titik-area'), titik = [...area.children];
  const render = () => {
    // r baris yang sama panjang (q titik), sisanya (merah) di baris tambahan.
    const r = st.r, q = Math.floor(n / r), sisa = n % r, nb = r + (sisa ? 1 : 0), c = Math.max(q, sisa);
    const W = area.clientWidth || 300, H = 190;
    const s = Math.min(34, (W - 10) / c, (H - 10) / nb);
    titik.forEach((el, i) => {
      const diSisa = i >= r * q, baris = diSisa ? r : Math.floor(i / q), kol = diSisa ? i - r * q : i % q;
      Object.assign(el.style, { width: (s * 0.72) + 'px', height: (s * 0.72) + 'px', left: (W - c * s) / 2 + kol * s + 'px', top: (H - nb * s) / 2 + baris * s + 'px' });
      el.classList.toggle('sisa', diSisa);
    });
    const info = host.querySelector('.wb-faktor-info');
    if (n % r === 0) {
      const baru = !temu.has(r) || !temu.has(n / r);
      temu.add(r); temu.add(n / r);
      info.innerHTML = `<b class="ok">${r} baris × ${n / r} titik = ${n}</b> → ${r} dan ${n / r} adalah faktor ${n}.`;
      host.querySelector('[data-chips]').innerHTML = [...temu].sort((x, y) => x - y).map(v => `<span class="chip ok">${v}</span>`).join('');
      if (baru) ctx.sfx.ok();
      const semua = divisors(n);
      if (semua.every(v => temu.has(v))) ctx.selesai(`Lengkap! Faktor ${n} adalah ${semua.join(', ')}. Faktor = bilangan yang membagi ${n} habis.`);
      else ctx.ucap(`Rapi! Sudah ${temu.size} dari ${semua.length} faktor. Coba banyak baris yang lain.`, 'cheer');
    } else {
      info.innerHTML = `<b class="salah">${n} : ${r} = ${Math.floor(n / r)} sisa ${n % r}</b> → barisnya tidak sama panjang, jadi ${r} bukan faktor ${n}.`;
      ctx.ucap('');
    }
  };
  pasangPengatur(host, st, () => { ctx.sfx.tap(); render(); });
  requestAnimationFrame(render);
}

// ==================================================================
// 15. Faktor sekutu & FPB: ketuk faktor yang dimiliki kedua bilangan.
//     param: { a: 12, b: 18 }
// ==================================================================
export function faktorSekutu(host, p, ctx) {
  const fa = divisors(p.a), fb = divisors(p.b), sekutu = fa.filter(v => fb.includes(v)), temu = new Set();
  const barisF = (n, f) => `<div class="wb-fbaris"><span class="chip-lbl">Faktor ${n}:</span><span class="wb-fchip">${f.map(v => `<button type="button" class="chip wb-chip" data-v="${v}">${v}</button>`).join('')}</span></div>`;
  host.innerHTML = `<div class="wg wb-sekutu">${barisF(p.a, fa)}${barisF(p.b, fb)}<div class="wb-faktor-info" aria-live="polite">Faktor sekutu: <b data-sekutu>–</b></div></div>`;
  ketuk(host, '[data-v]', b => {
    const v = +b.dataset.v;
    if (temu.has(v)) return;
    if (!sekutu.includes(v)) {
      ctx.sfx.no(); goyang(b);
      ctx.ucap(`${v} hanya faktor ${p.a % v === 0 ? p.a : p.b}, bukan faktor ${p.a % v === 0 ? p.b : p.a}.`, 'think');
      return;
    }
    temu.add(v);
    host.querySelectorAll(`[data-v="${v}"]`).forEach(x => { x.classList.add('cm'); letup(x); });
    ctx.sfx.tap();
    host.querySelector('[data-sekutu]').textContent = [...temu].sort((x, y) => x - y).join(', ');
    if (temu.size === sekutu.length) {
      const g = Math.max(...sekutu);
      host.querySelectorAll(`[data-v="${g}"]`).forEach(x => { x.classList.add('hl'); });
      ctx.selesai(`Faktor sekutu ${p.a} dan ${p.b}: ${sekutu.join(', ')}. Yang terbesar adalah **${g}**, jadi FPB(${p.a}, ${p.b}) = ${g}.`);
    } else ctx.ucap(`${v} ada di kedua daftar! Cari ${sekutu.length - temu.size} lagi.`, 'cheer');
  });
}

// ==================================================================
// 16. Pilah soal cerita: KPK atau FPB?
//     param: { kartu: [{ teks, jenis: 'kpk'|'fpb', alasan }] }
// ==================================================================
export function pilahCerita(host, p, ctx) {
  let i = 0;
  host.innerHTML = `<div class="wg wb-pilah">
    <div class="wb-cerita" aria-live="polite"></div>
    <div class="wg-tombol"><button type="button" class="btn wb-ke kpk" data-ke="kpk">KPK</button><button type="button" class="btn wb-ke fpb" data-ke="fpb">FPB</button></div>
    <div class="wb-keranjang"><div class="kpk"><b>KPK</b><ul data-isi="kpk"></ul></div><div class="fpb"><b>FPB</b><ul data-isi="fpb"></ul></div></div>
  </div>`;
  const cerita = host.querySelector('.wb-cerita');
  const tampil = () => {
    cerita.innerHTML = i < p.kartu.length ? `<span class="wb-nomor-kartu">Kartu ${i + 1}/${p.kartu.length}</span>${p.kartu[i].teks}` : '<b>Semua kartu sudah dipilah!</b>';
    animasi(cerita, [{ opacity: 0, transform: 'translateY(-10px) rotate(-2deg)' }, { opacity: 1, transform: 'none' }], { duration: 300 });
  };
  ketuk(host, '[data-ke]', async b => {
    if (i >= p.kartu.length) return;
    const k = p.kartu[i];
    if (b.dataset.ke !== k.jenis) { ctx.sfx.no(); goyang(cerita); ctx.ucap(k.petunjuk || 'Perhatikan kata kuncinya lagi.', 'think'); return; }
    const ul = host.querySelector(`[data-isi="${k.jenis}"]`);
    await terbang(cerita, ul.parentNode, 'kartu-cerita');
    ul.insertAdjacentHTML('beforeend', `<li>${k.singkat || k.teks}</li>`);
    ctx.sfx.ok();
    ctx.ucap(k.alasan, 'cheer');
    i++;
    tampil();
    if (i === p.kartu.length) ctx.selesai('Hebat! **Bersamaan lagi / bertemu lagi** → KPK. **Dibagi sama banyak, sebanyak-banyaknya** → FPB.');
  });
  tampil();
}

// ==================================================================
// 17. Urutan operasi hitung: ketuk operasi yang dikerjakan lebih dulu.
//     param: { ekspresi: '12 + 6 × 3 − 8 : 2' }
// ==================================================================
const HITUNG = { '+': (x, y) => x + y, '−': (x, y) => x - y, '×': (x, y) => x * y, ':': (x, y) => x / y };
export function urutanOperasi(host, p, ctx) {
  let t = p.ekspresi.match(/\d+|[+−×:()]/g).map(s => (/\d/.test(s) ? Number(s) : s));
  const adaKurung = t.includes('(');
  const riwayat = [];
  host.innerHTML = `<div class="wg wb-operasi">
    <div class="wb-ekspresi" aria-live="polite"></div>
    <ol class="wb-riwayat"></ol>
    <p class="wb-catatan">Ketuk tanda operasi yang harus dikerjakan <b>lebih dulu</b>.</p>
  </div>`;
  const eks = host.querySelector('.wb-ekspresi'), ol = host.querySelector('.wb-riwayat');
  const teksT = arr => arr.map(x => (typeof x === 'number' ? fmt(x) : x)).join(' ').replace(/\( /g, '(').replace(/ \)/g, ')');
  // Indeks operasi yang benar berikutnya.
  const berikut = () => {
    let a = 0, b = t.length;
    // Kurung terdalam yang pertama.
    for (let i = 0; i < t.length; i++) if (t[i] === '(') a = i; else if (t[i] === ')') { b = i; break; }
    if (b === t.length) a = 0; else a += 1;
    const rentang = [];
    for (let i = a; i < b; i++) if (typeof t[i] === 'string' && '+−×:'.includes(t[i])) rentang.push(i);
    return rentang.find(i => '×:'.includes(t[i])) ?? rentang[0];
  };
  const alasan = i => {
    const benar = berikut();
    const dalamKurung = j => { let d = 0; for (let k = 0; k < j; k++) { if (t[k] === '(') d++; if (t[k] === ')') d--; } return d > 0; };
    if (t.includes('(') && !dalamKurung(i)) return 'Yang di dalam **kurung** dikerjakan paling dulu!';
    if ('+−'.includes(t[i]) && '×:'.includes(t[benar])) return '**Kali dan bagi** dikerjakan lebih dulu daripada tambah dan kurang.';
    return 'Operasi yang setingkat dikerjakan **dari kiri** ke kanan.';
  };
  const render = (sorot = -1) => {
    eks.innerHTML = t.map((x, i) => typeof x === 'number' ? `<span class="wb-angka-op${i === sorot ? ' hasil' : ''}">${fmt(x)}</span>`
      : x === '(' || x === ')' ? `<span class="wb-kurung">${x}</span>`
      : `<button type="button" class="wb-op-tombol" data-op="${i}" aria-label="operasi ${x}">${x}</button>`).join('');
  };
  ketuk(host, '[data-op]', async b => {
    const i = +b.dataset.op;
    if (i !== berikut()) { ctx.sfx.no(); goyang(b); ctx.ucap(alasan(i), 'think'); return; }
    const [x, op, y] = [t[i - 1], t[i], t[i + 1]], hasil = HITUNG[op](x, y);
    b.classList.add('benar');
    eks.querySelectorAll('.wb-angka-op').forEach(el => el.classList.remove('sorot'));
    const kiri = b.previousElementSibling, kanan = b.nextElementSibling;
    kiri.classList.add('sorot'); kanan.classList.add('sorot');
    ctx.sfx.tap();
    await jeda(380);
    if (gerakHalus()) await Promise.all([kiri, b, kanan].map(el => el.animate([{ transform: 'scale(1)', opacity: 1 }, { transform: 'scale(.6)', opacity: 0 }], { duration: 220, fill: 'forwards' }).finished.catch(() => {})));
    t.splice(i - 1, 3, hasil);
    let idx = i - 1;
    // Buang kurung yang tinggal berisi satu bilangan.
    for (let k = 1; k < t.length - 1; k++) if (t[k - 1] === '(' && t[k + 1] === ')' && typeof t[k] === 'number') { t.splice(k + 1, 1); t.splice(k - 1, 1); if (k <= idx) idx--; }
    riwayat.push(`${fmt(x)} ${op} ${fmt(y)} = ${fmt(hasil)}`);
    ol.insertAdjacentHTML('beforeend', `<li><span>${riwayat.at(-1)}</span><span class="muted">→ ${teksT(t)}</span></li>`);
    render(idx);
    letup(eks.querySelector('.wb-angka-op.hasil') || eks);
    if (t.length === 1) ctx.selesai(`Hasilnya **${fmt(t[0])}**. Urutannya: ${adaKurung ? 'isi kurung dulu, lalu ' : ''}kali/bagi, baru tambah/kurang, yang setingkat dari kiri.`);
    else ctx.ucap(`${fmt(x)} ${op} ${fmt(y)} = ${fmt(hasil)}. Lanjut ke operasi berikutnya!`, 'happy');
  });
  render();
}

// ==================================================================
// 18. Model batang soal cerita: pilih kalimat matematika yang cocok.
//     param: { cerita, kelompok, isi, satuan, kurang, labelKurang, pilihan: [..], kunci, langkah: [..] }
// ==================================================================
export function modelBatang(host, p, ctx) {
  let hidup = true;
  host.innerHTML = `<div class="wg wb-model">
    <p class="wb-cerita-teks">${p.cerita}</p>
    <div class="wb-model-batang" aria-hidden="true"></div>
    <div class="wb-model-ket" aria-live="polite"></div>
    <p class="wb-catatan">Kalimat matematika yang cocok:</p>
    <div class="wb-pilih-ekspresi">${p.pilihan.map((x, j) => `<button type="button" class="btn wb-eks" data-j="${j}">${x}</button>`).join('')}</div>
    <ol class="wb-riwayat" hidden></ol>
  </div>`;
  const bt = host.querySelector('.wb-model-batang');
  (async () => {
    bt.innerHTML = Array.from({ length: p.kelompok }, (_, i) => `<span class="wb-seg" style="--i:${i}">${p.isi}</span>`).join('') + (p.kurang ? `<span class="wb-seg kurang" style="--i:${p.kelompok}">−${p.kurang}</span>` : '');
    const segs = [...bt.children];
    for (const s of segs) { if (!hidup) return; s.classList.add('muncul'); await jeda(220); }
    host.querySelector('.wb-model-ket').innerHTML = `${p.kelompok} ${p.satuan} × ${p.isi}${p.kurang ? `, lalu ${p.labelKurang} ${p.kurang}` : ''}`;
  })();
  ketuk(host, '[data-j]', async b => {
    const j = +b.dataset.j;
    if (j !== p.kunci) { ctx.sfx.no(); goyang(b); ctx.ucap(p.salah?.[j] || 'Belum cocok dengan ceritanya. Lihat lagi model batangnya.', 'think'); return; }
    host.querySelectorAll('[data-j]').forEach(x => { x.disabled = true; x.classList.toggle('btn-go', x === b); });
    const ol = host.querySelector('.wb-riwayat');
    ol.hidden = false;
    for (const l of p.langkah) { if (!hidup) return; ol.insertAdjacentHTML('beforeend', `<li>${l}</li>`); animasi(ol.lastElementChild, [{ opacity: 0, transform: 'translateX(-10px)' }, { opacity: 1, transform: 'none' }]); await jeda(350); }
    ctx.selesai(p.pesan || `Tepat! ${p.pilihan[p.kunci]} = **${fmt(p.hasil)}**.`);
  });
  return () => { hidup = false; };
}

export const WIDGET_BILANGAN = {
  garisPecahan, bandingPecahan, urutkanPecahan, kisi100, rantaiKonversi, labelHarga,
  gabungPecahan, samakanPenyebut, campuran, modelLuas, bagiPecahan, balikPecahan,
  lompatKatak, susunFaktor, faktorSekutu, pilahCerita, urutanOperasi, modelBatang,
};
